import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrganization } from '../contexts/OrganizationContext'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { isDemoOrg } from '../lib/demo'

interface SetupWizardGuardProps {
  children: React.ReactNode
}

export default function SetupWizardGuard({ children }: SetupWizardGuardProps) {
  const navigate = useNavigate()
  const { organization, loading: orgLoading } = useOrganization()
  const { isAdmin, loading: authLoading } = useAuth()
  const [checking, setChecking] = useState(true)
  const [needsSetup, setNeedsSetup] = useState(false)

  // Failsafe: if loading takes more than 10 seconds, stop blocking
  useEffect(() => {
    const failsafeTimeout = setTimeout(() => {
      if (checking) {
        console.log('SetupWizardGuard failsafe: loading took too long, continuing...')
        setChecking(false)
      }
    }, 10000)
    return () => clearTimeout(failsafeTimeout)
  }, [checking])

  useEffect(() => {
    const checkSetupNeeded = async () => {
      // Wait for auth and org to finish loading
      if (authLoading || orgLoading) {
        return
      }

      // If not admin or no org, skip check
      if (!organization?.id || !isAdmin) {
        setChecking(false)
        return
      }

      // Demo org never needs setup
      if (isDemoOrg(organization.id)) {
        setChecking(false)
        return
      }

      try {
        // Call the RPC function to check if setup is needed with a timeout
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout

        const { data, error } = await supabase
          .rpc('organization_needs_setup', {
            p_organization_id: organization.id
          })
          .abortSignal(controller.signal)

        clearTimeout(timeoutId)

        if (error) {
          // If it's an abort error or any other error, just continue without blocking
          if (error.message?.includes('abort') || error.code === '20') {
            console.log('Setup check timed out, continuing...')
          } else {
            console.error('Error checking setup status:', error)
          }
          setChecking(false)
          return
        }

        setNeedsSetup(data === true)
        setChecking(false)

        // If setup is needed and we're not already on the setup or welcome page, redirect
        if (data === true && !window.location.pathname.includes('/setup') && !window.location.pathname.includes('/welcome')) {
          navigate('/welcome')
        }

        // If we're on the setup or welcome page, always allow access (for re-running)
        if (window.location.pathname.includes('/setup') || window.location.pathname.includes('/welcome')) {
          setNeedsSetup(false)
        }
      } catch (error: any) {
        // Handle any errors including abort errors - don't block the page
        if (error?.name === 'AbortError' || error?.message?.includes('abort')) {
          console.log('Setup check timed out, continuing...')
        } else {
          console.error('Error checking setup status:', error)
        }
        setChecking(false)
      }
    }

    checkSetupNeeded()
  }, [organization?.id, isAdmin, navigate, authLoading, orgLoading])

  // Show loading state while checking (or while auth/org still loading)
  if (checking || authLoading || orgLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#000',
        color: '#9CA3AF'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{
            width: '48px',
            height: '48px',
            margin: '0 auto 1rem',
            border: '4px solid rgba(156, 163, 175, 0.3)',
            borderTopColor: '#9CA3AF',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <p>Checking organization setup...</p>
        </div>
        <style>{`
          @keyframes spin {
            to {
              transform: rotate(360deg);
            }
          }
        `}</style>
      </div>
    )
  }

  // If setup is needed and we're not on the setup or welcome page, don't render children
  if (needsSetup && !window.location.pathname.includes('/setup') && !window.location.pathname.includes('/welcome')) {
    return null
  }

  return <>{children}</>
}